import { useState, useEffect, useRef } from "react";
import SalesInputRow from "../molecules/SalesInputRow";
import ResultRow from "../molecules/ResultRow";
import ActionButton from "../atoms/ActionButton";
import {
  fetchDailyReport,
  saveDailyReport,
} from "../../services/dailyReportService";
import {
  getCurrentUser,
  getCurrentPerson,
  setCurrentPerson,
} from "../../services/authService";
import InputField from "../atoms/InputField";
import TextAreaField from "../atoms/TextAreaField";
import CalendarButton from "../atoms/CalendarButton";
import DatePickerField from "../molecules/DatePickerField";
import DateNavigator from "../molecules/DateNavigator";
// 誤入力防止機能関連 (一時無効) - クライアント要望により非表示化（2026-06-17）
// import JournalCheckRows from "../molecules/JournalCheckRows";
// import ConfirmModal from "../molecules/ConfirmModal";

/**
 * 売上入力項目の定義
 * クレジット・ギフト券・商品券・電子マネーは UI から削除済み（DBカラムは保持）
 *
 * SQL列との対応:
 *   cash        → GenkinUriage
 *   kake        → KakeUriage
 *   sonota      → SonotaUriage
 *   syohizei    → SyohiZei
 *   sonotakoujo → SonotaKoujo
 */
const SALES_ITEMS = [
  { key: "cash", label: "現金売上" },
  { key: "kake", label: "掛売上" },
  { key: "sonota", label: "その他売上" },
];

const DEDUCTION_ITEMS = [
  { key: "syohizei", label: "うち消費税" },
  // その他控除関連 (一時無効)
  // { key: "sonotakoujo", label: "その他控除" },
];

// 全入力項目をまとめた配列 (初期 state の構築・DB送信時に使用)
const ALL_INPUT_ITEMS = [...SALES_ITEMS, ...DEDUCTION_ITEMS];

// DB列名 → React state key のマッピング (既存データ読み込み時に使用)
const DB_TO_STATE = {
  GenkinUriage: "cash",
  KakeUriage: "kake",
  SonotaUriage: "sonota",
  SyohiZei: "syohizei",
  // その他控除関連 (一時無効)
  // SonotaKoujo:     "sonotakoujo",
  GenkinAridaka: "genkinAridaka",
  Message: "message",
};

const buildInitialState = () => {
  const state = { custNo: "", genkinAridaka: "", message: "" };
  ALL_INPUT_ITEMS.forEach(({ key }) => {
    state[key] = "";
  });
  return state;
};

/**
 * 文字列を整数に変換する。空文字・非数字は 0 として扱う。
 */
const toInt = (v) => parseInt(v, 10) || 0;

// YYYYMMDD 文字列 → Date オブジェクト（ローカル時刻）
const parseYYYYMMDD = (str) =>
  new Date(
    parseInt(str.slice(0, 4), 10),
    parseInt(str.slice(4, 6), 10) - 1,
    parseInt(str.slice(6, 8), 10),
  );

// Date オブジェクト → YYYYMMDD 文字列
const formatYYYYMMDD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
};

/**
 * StoreInputForm で使用するサイズ設定
 * 変更する場合はここを編集する
 */
const INPUT_STYLE = {
  labelClass: "px-4 py-4 text-lg font-bold text-right whitespace-nowrap",
  inputCellClass: "px-4 py-4",
  amountWidthClass: "w-64",
  amountSizeClass: "px-3 py-2 text-base",
  amountUnit: "円",
  amountUnitClass: "text-base",
  // 税率関連 (一時無効)
  // taxWidthClass: "w-20",
  // taxSizeClass:  "px-3 py-2 text-base",
  // taxUnit:       "%",
  // taxUnitClass:  "text-base",
};

const RESULT_STYLE = {
  labelClass: "px-4 py-4 text-lg text-right whitespace-nowrap",
  cellClass: "px-4 py-4",
  widthClass: "w-64",
  sizeClass: "px-3 py-2 text-base",
  unit: "円",
  unitClass: "text-base",
};

// 客数入力欄のスタイル（売上入力行と共通化するため INPUT_STYLE を流用）
const CUST_STYLE = {
  labelClass: INPUT_STYLE.labelClass,
  inputCellClass: INPUT_STYLE.inputCellClass,
  amountWidthClass: INPUT_STYLE.amountWidthClass,
  amountSizeClass: INPUT_STYLE.amountSizeClass,
  amountUnit: "人",
  amountUnitClass: INPUT_STYLE.amountUnitClass,
};

/**
 * StoreInputForm - 店舗売上入力フォーム
 *
 * @param {string}   date         - 表示・保存する日付 (YYYYMMDD形式)。App.js から渡される。
 * @param {function} onDateChange - 日付変更時のコールバック。カレンダー選択時に呼ばれる。
 */
// DBデータからフォーム state を組み立てる（キャッシュ・API共通）
const applyDataToState = (data) => {
  if (!data) return buildInitialState();
  const newState = buildInitialState();
  Object.entries(DB_TO_STATE).forEach(([dbCol, stateKey]) => {
    if (data[dbCol] != null) newState[stateKey] = String(data[dbCol]);
  });
  if (data.CustNo != null) newState.custNo = String(data.CustNo);
  return newState;
};

function StoreInputForm({ date, onDateChange }) {
  const [formData, setFormData] = useState(buildInitialState);
  // 誤入力防止機能関連 (一時無効) - クライアント要望により非表示化（2026-06-17）。コードは将来の再有効化に備えて保持
  // ジャーナル照合用手入力売上額 (DBには保存しない検証用)
  // const [journalAmount, setJournalAmount] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // 担当者名: sessionStorage の currentPerson を初期値とする
  const [person, setPerson] = useState(getCurrentPerson);
  // 誤入力防止機能の省略設定: localStorage でデバイス単位に永続化する（デフォルトは省略状態）
  // const [omitDiffCheck, setOmitDiffCheck] = useState(
  //   () => localStorage.getItem("omitDiffCheck") !== "false"
  // );
  // 差額確認モーダルの表示状態
  // const [modalOpen, setModalOpen] = useState(false);
  // const [modalType, setModalType] = useState(null);
  // セッション内キャッシュ: Map<YYYYMMDD, DBデータ|null>
  const cache = useRef(new Map());
  // マウント時・日付変更時に既存データを取得してフォームに反映する
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;

    // setJournalAmount(""); // 誤入力防止機能 (一時無効)

    // キャッシュヒット: APIを呼ばず即座に反映する
    if (cache.current.has(date)) {
      setFormData(applyDataToState(cache.current.get(date)));
      return;
    }

    // キャッシュミス: フォームをリセットしてAPIから取得する
    setFormData(buildInitialState());
    fetchDailyReport(date, user.shopNo, user.regiNo)
      .then((data) => {
        cache.current.set(date, data); // null（未保存日）もキャッシュする
        setFormData(applyDataToState(data));
      })
      .catch((err) => console.error("日報データ取得失敗:", err));
  }, [date]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // 担当者名変更: state と sessionStorage を同時に更新する
  const handlePersonChange = (e) => {
    const name = e.target.value;
    setPerson(name);
    setCurrentPerson(name);
  };

  // 省略チェックボックス変更 (一時無効) - クライアント要望により非表示化（2026-06-17）
  // const handleOmitCheckChange = (e) => {
  //   const checked = e.target.checked;
  //   setOmitDiffCheck(checked);
  //   localStorage.setItem("omitDiffCheck", String(checked));
  //   // 省略に切り替えたとき売上額をリセットしてモーダルが誤作動しないようにする
  //   if (checked) setJournalAmount("");
  // };

  // 日付を delta 日分移動する
  const moveDay = (delta) => {
    const d = parseYYYYMMDD(date);
    d.setDate(d.getDate() + delta);
    onDateChange(formatYYYYMMDD(d));
  };

  // 総売上 (SoUriage) = SALES_ITEMS の合計
  const soUriage = SALES_ITEMS.reduce(
    (sum, { key }) => sum + toInt(formData[key]),
    0,
  );

  // 純売上 (JunUriage) = 総売上 - 消費税 - その他控除
  const junUriage =
    soUriage - toInt(formData.syohizei) - toInt(formData.sonotakoujo);

  // 差額計算 (一時無効) - クライアント要望により非表示化（2026-06-17）
  // 差額 = 手入力売上額 - 算出値 (0 なら誤入力なし)
  // const soUriageDiff = toInt(journalAmount) - soUriage;
  // const junUriageDiff = toInt(journalAmount) - junUriage;

  // 現金相違額 = 現金売上 - 入金機投入額
  const genkinKabusoku = toInt(formData.cash) - toInt(formData.genkinAridaka);

  // 実際の DB 保存処理（確定ボタン直接 or モーダル「確定する」から呼ばれる）
  const doSave = async () => {
    const user = getCurrentUser();
    if (!user) return;

    // setModalOpen(false); // 誤入力防止機能 (一時無効)
    setIsSaving(true);
    try {
      await saveDailyReport({
        YYYYMMDD: date,
        ShopNo: user.shopNo,
        RegiNo: user.regiNo,
        ShopName: user.shopName,
        InputPerson: person,
        // UI非表示項目は null を送信（DBカラムは保持）
        GenkinUriage: toInt(formData.cash),
        CreditUriage: null,
        GiftkenUriage: null,
        SyohinkenUriage: null,
        EmoneyUriage: null,
        KakeUriage: toInt(formData.kake),
        SonotaUriage: toInt(formData.sonota),
        SyohiZei: toInt(formData.syohizei),
        SonotaKoujo: toInt(formData.sonotakoujo),
        SoUriage: soUriage,
        JunUriage: junUriage,
        CustNo: formData.custNo !== "" ? toInt(formData.custNo) : null,
        GenkinAridaka:
          formData.genkinAridaka !== "" ? toInt(formData.genkinAridaka) : null,
        Message: formData.message !== "" ? formData.message : null,
      });
      // 確定成功後にキャッシュを更新する（次回同日に戻った際に最新データが即表示される）
      cache.current.set(date, {
        GenkinUriage: toInt(formData.cash),
        KakeUriage: toInt(formData.kake),
        SonotaUriage: toInt(formData.sonota),
        SyohiZei: toInt(formData.syohizei),
        SonotaKoujo: toInt(formData.sonotakoujo),
        SoUriage: soUriage,
        JunUriage: junUriage,
        CustNo: formData.custNo !== "" ? toInt(formData.custNo) : null,
        GenkinAridaka:
          formData.genkinAridaka !== "" ? toInt(formData.genkinAridaka) : null,
        Message: formData.message !== "" ? formData.message : null,
      });
      alert("保存しました");
    } catch (err) {
      console.error("日報保存失敗:", err);
      alert(`保存に失敗しました: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 確定ボタン押下
  // 誤入力防止機能 (一時無効) - クライアント要望により非表示化（2026-06-17）。元の差額チェック分岐は将来の再有効化に備えて保持
  // const handleConfirm = () => {
  //   if (!omitDiffCheck) {
  //     if (journalAmount === "") {
  //       setModalType("noCheck");
  //       setModalOpen(true);
  //       return;
  //     }
  //     if (soUriageDiff !== 0 && junUriageDiff !== 0) {
  //       setModalType("diffFound");
  //       setModalOpen(true);
  //       return;
  //     }
  //   }
  //   doSave();
  // };
  const handleConfirm = () => doSave();

  return (
    <div className="p-6">
      {/* 差額確認モーダル (一時無効) - クライアント要望により非表示化（2026-06-17）
      {modalOpen && (
        <ConfirmModal
          type={modalType}
          soUriageDiff={soUriageDiff}
          junUriageDiff={junUriageDiff}
          onConfirm={doSave}
          onCancel={() => setModalOpen(false)}
        />
      )}
      */}

      {/* 日付ナビゲーションと担当者名・省略設定を横並び配置 */}
      <div className="flex items-center gap-28 mb-4">
        {/* 左: 前日◁ カレンダー ▷翌日 */}
        <DateNavigator
          prevLabel="◀ 前日"
          nextLabel="翌日 ▶"
          onPrev={() => moveDay(-1)}
          onNext={() => moveDay(1)}
        >
          <DatePickerField
            selected={parseYYYYMMDD(date)}
            onChange={(d) => onDateChange(formatYYYYMMDD(d))}
            customInput={<CalendarButton yyyyValue={date} showDay />}
            dateFormat="yyyy/MM/dd"
            locale="ja"
            dateFormatCalendar="yyyy MMMM"
            calendarClassName="datepicker-date-picker"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            enableTabLoop={false}
          />
        </DateNavigator>

        {/* 右: 担当者名 + 省略設定 */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-base text-gray-600 whitespace-nowrap">
              担当者
            </span>
            <InputField
              value={person}
              onChange={handlePersonChange}
              numericOnly={false}
              textAlignClass="text-left"
              widthClass="w-36"
              sizeClass="px-3 py-2 text-base"
              borderClass="border-2 border-gray-400 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
          {/* 誤入力防止機能の省略チェックボックス (一時無効) - クライアント要望により非表示化（2026-06-17）
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={omitDiffCheck}
              onChange={handleOmitCheckChange}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm text-gray-600 whitespace-nowrap">誤入力防止機能を省略する</span>
          </label>
          */}
        </div>
      </div>

      {/* 入力項目〜算出結果・メッセージ欄・確定ボタンを同じ幅で揃えるためのコンテナ */}
      <div className="inline-block mt-4">
        <div className="flex gap-12">
          {/* 左側: 入力項目 */}
          <div>
            <h2 className="px-4 py-2 text-base text-center">入力項目</h2>
            <table>
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="px-4 py-2 text-base font-normal text-center">
                    項目
                  </th>
                  <th className="px-4 py-2 text-base font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {ALL_INPUT_ITEMS.map(({ key, label }) => (
                  <SalesInputRow
                    key={key}
                    label={label}
                    amount={formData[key]}
                    onAmountChange={(e) => handleChange(key, e.target.value)}
                    {...INPUT_STYLE}
                  />
                ))}
                {/* 区切り線 */}
                <tr>
                  <td colSpan={2} className="py-1 border-t border-gray-200" />
                </tr>
                {/* 客数 */}
                <SalesInputRow
                  label="客数"
                  amount={formData.custNo}
                  onAmountChange={(e) => handleChange("custNo", e.target.value)}
                  {...CUST_STYLE}
                />
                {/* 入金機投入額 (DB列名は GenkinAridaka) */}
                <SalesInputRow
                  label="入金機投入額"
                  amount={formData.genkinAridaka}
                  onAmountChange={(e) =>
                    handleChange("genkinAridaka", e.target.value)
                  }
                  {...INPUT_STYLE}
                />
              </tbody>
            </table>
          </div>

          {/* 右側: 算出結果 */}
          <div>
            <h2 className="px-4 py-2 text-base text-center">算出結果</h2>
            <table>
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="px-4 py-2 text-base font-normal text-center">
                    項目
                  </th>
                  <th className="px-4 py-2 text-base font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {/* 算出売上 */}
                <ResultRow label="総売上" value={soUriage} {...RESULT_STYLE} />
                <ResultRow label="純売上" value={junUriage} {...RESULT_STYLE} />
                {/* 現金相違額 = 現金売上 - 入金機投入額。非ゼロのとき赤色表示 */}
                <ResultRow
                  label="現金相違額"
                  value={genkinKabusoku}
                  signed
                  valueColorClass={genkinKabusoku !== 0 ? "text-red-600" : ""}
                  {...RESULT_STYLE}
                />

                {/* ジャーナル照合 (一時無効) - クライアント要望により非表示化（2026-06-17）
                <JournalCheckRows
                  journalAmount={journalAmount}
                  onJournalChange={(e) => setJournalAmount(e.target.value)}
                  soUriageDiff={soUriageDiff}
                  junUriageDiff={junUriageDiff}
                  inputStyle={INPUT_STYLE}
                  resultStyle={RESULT_STYLE}
                  visible={!omitDiffCheck}
                />
                */}
              </tbody>
            </table>
          </div>
        </div>

        {/* メッセージ欄: 入力項目・算出結果の両列に跨る横幅の行 */}
        <div className="mt-6 px-4">
          <div className="text-lg text-gray-600 mb-1">
            事務所へのメッセージ
          </div>
          <TextAreaField
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            maxLength={100}
            rows={2}
            widthClass="w-full"
          />
        </div>

        {/* 確定ボタン */}
        <div className="flex justify-end mt-4 pt-4 pr-4">
          <ActionButton
            label={isSaving ? "保存中..." : "確定"}
            onClick={handleConfirm}
            disabled={isSaving}
            paddingClass="px-10 py-3 font-medium"
            textSizeClass="text-lg"
          />
        </div>
      </div>
    </div>
  );
}

export default StoreInputForm;
